<script setup lang="ts">
import { type ComponentPublicInstance, computed, nextTick, onMounted, ref, useTemplateRef, watch, watchEffect } from "vue";

import debounce from "lodash/debounce";
import InputMask from "primevue/inputmask";

import { isset, pick, sanitizeObject } from "@/helpers/object";

import { validate } from "@/dsComponents/inputs/helpers";
import Loader from "@/dsComponents/Loader.vue";

interface TextInputAltProps {
    modelValue: string;
    type?: string;
    placeholder?: string;
    size?: "xs" | "sm" | "md" | "lg";
    variant?: "ghost" | "stroke" | "transparent" | "grey" | "grey2";
    state?: "empty" | "filled";
    disabled?: boolean;
    validate?: boolean;
    debounce?: number;
    iconLeft?: string;
    iconRight?: string;
    loading?: boolean;
    error?: boolean | string;
    focused?: boolean;
    width?: string;
    step?: number;
    min?: number;
    name?: string;
    readonly?: boolean;
    rows?: number;
    inputAlign?: "left" | "center" | "right";
    autocomplete?: string;
}

const props = withDefaults(defineProps<TextInputAltProps>(), {
    variant: "ghost",
    size: "xs",
    debounce: 0,
    type: "text",
    readonly: false,
    inputAlign: "left",
    autocomplete: "off",
});

const emit = defineEmits<{
    (e: "update:modelValue", value: string, event?: Event): void;
    (e: "blur", event: FocusEvent): void;
    (e: "focus"): void;
    (e: "left-icon-click"): void;
    (e: "right-icon-click"): void;
}>();

const focus = ref(false);
const localError = ref<string | undefined>("");
const localValid = ref<boolean | undefined>(false);
watchEffect(() => {
    localValid.value = props.validate;
});

const htmlInputProps = computed(() => sanitizeObject(pick(props, ["type", "placeholder", "disabled", "width", "step", "min", "name", "autocomplete"])));

const isMultiline = computed(() => props.rows != null && props.rows >= 1);

const textareaProps = computed(() => sanitizeObject(pick(props, ["placeholder", "disabled", "name", "readonly", "rows"])));

const handleChange = debounce((event: Event) => {
    const inputValue: string = event?.target?.value ?? "";

    if (props.type === "currency") {
        const split = inputValue.split(" €");
        if (inputValue === "" || split.every((e: string) => e === "")) {
            emit("update:modelValue", "", event);
            return;
        }
        emit("update:modelValue", `${split[0]} €`, event);
        nextTick(() => {
            inputAlt.value?.focus();
            inputAlt.value?.setSelectionRange(split[0].length, split[0].length);
        });
    } else {
        emit("update:modelValue", inputValue, event);
    }
    if (localError.value && inputValue && props.validate) {
        const { error, isValid } = validate(inputValue, props.type, false);
        localError.value = error;
        localValid.value = isValid;
    }
}, props.debounce);

const handleBlur = (e: FocusEvent) => {
    focus.value = false;
    localValid.value = false;
    emit("blur", e);
    if (props.validate) {
        // Validate field on blur according with the type
        const { error, isValid } = validate(props.modelValue, props.type, false);
        localError.value = error;
        localValid.value = isValid;
    }
};

const handleFocus = () => {
    if (props.readonly || props.disabled) return;
    emit("focus");
    focus.value = true;
};

const writingLocker = (event: KeyboardEvent) => {
    const hasSelection = !!event.view?.getSelection()?.toString();
    switch (props.type) {
        case "currency":
            if (/^\d$/.test(event.key)) return;
            event.preventDefault();
            break;
        case "nir":
            if (event.target?.value.length < 18 && /^\d$/.test(event.key)) return;
            if (event.target?.value.length === 18 && hasSelection) return;
            event.preventDefault();
            break;
        case "evoliaId":
            if (event.target?.value.length < 7 && /^\d$/.test(event.key)) return;
            if (event.target?.value.length === 7 && hasSelection) return;
            event.preventDefault();
            break;
    }
};

const hasPhoneMaskInitialized = ref(false);

const handlePhoneChange = (value: string) => {
    const nextValue = value ?? "";
    if (nextValue === props.modelValue) return;
    if (!hasPhoneMaskInitialized.value) {
        hasPhoneMaskInitialized.value = true;
        if (!nextValue && props.modelValue) return;
    }
    emit("update:modelValue", nextValue);
};

const inputAlt = useTemplateRef("inputAlt");
const inputMask = useTemplateRef("inputMask");

const focusInput = () => {
    nextTick(() => {
        (inputAlt.value as HTMLInputElement)?.focus();
        (inputMask.value as ComponentPublicInstance)?.$el?.focus();
    });
};
watch(
    () => props.focused,
    () => {
        focusInput();
    }
);

onMounted(() => {
    if (props.focused) {
        focusInput();
    }
});
</script>
<template>
    <div
        class="ds-text-input-alt"
        :class="[
            size,
            variant,
            state,
            { error: isset(localError) || error, focus, disabled, readonly: props.readonly, multiline: isMultiline },
            `align-${inputAlign}`,
        ]"
        :style="sanitizeObject({ width })"
    >
        <i v-if="iconLeft" :class="['icon-left', 'ti', `ti-${iconLeft}`, { focus }]" @click="emit('left-icon-click')"></i>
        <textarea
            v-if="isMultiline"
            v-bind="textareaProps"
            ref="inputAlt"
            :value="modelValue"
            @input="handleChange"
            @blur="handleBlur"
            @focus="handleFocus"
        />
        <input
            v-else-if="type !== 'tel'"
            v-bind="htmlInputProps"
            ref="inputAlt"
            :value="modelValue"
            :readonly="props.readonly"
            @input="handleChange"
            @keypress="writingLocker"
            @blur="handleBlur"
            @focus="handleFocus"
        />
        <InputMask
            v-else
            ref="inputMask"
            v-bind="htmlInputProps"
            :model-value="modelValue"
            mask="99 99 99 99 99"
            fluid
            slotChar=""
            :autoClear="false"
            @update:model-value="handlePhoneChange"
        />
        <Loader v-if="loading" size="sm" />
        <i v-else-if="iconRight" :class="['icon-right', 'ti', `ti-${iconRight}`]" @click="emit('right-icon-click')"></i>
        <i v-if="localValid" class="ti ti-check"></i>
    </div>
</template>
<style lang="scss" scoped>
@mixin inputShadow($color) {
    box-shadow: 0 0 0 3px $color;
}

$transition-time: 0.2s;

.ds-text-input-alt {
    display: flex;
    height: 36px;
    padding: 0;
    overflow: hidden;
    width: 100%;
    gap: 8px;
    align-items: center;

    &:not(.transparent) {
        padding: 0 var(--spacing-lg, 12px);
    }

    border-radius: 6px;
    transition: $transition-time;

    // .ghost, .transparent
    border: 1px solid transparent;
    &.stroke {
        border: 1px solid $stroke-soft-200;
    }
    &.grey,
    &.grey2 {
        border: none;
    }

    .ti {
        font-size: 14px;
        margin: auto 0;
    }

    .ti-check {
        color: $blue-base;
        font-weight: 600;
    }

    .icon-left {
        color: $icon-soft-400;
        transition: $transition-time;
        &.focus {
            color: $icon-strong-900;
        }
    }

    .icon-right {
        cursor: pointer;
    }

    &.disabled {
        .ti-check {
            color: $stroke-sub-300;
        }
        .icon-right {
            cursor: default;
            pointer-events: none;
        }
    }

    input {
        border: none;
        outline: none;
        padding: 0;
        background-color: transparent;
        color: $text-main-900;
        font-weight: 500;
        width: 100%;
        font-size: inherit;
        line-height: inherit;
        &::placeholder {
            font-size: inherit;
            line-height: inherit;
            color: $text-soft-400;
            opacity: 1;
        }
        &[type="date"] {
            -moz-appearance: textfield;
            appearance: textfield;
        }
        &[type="date"]::-webkit-calendar-picker-indicator {
            display: none;
            -webkit-appearance: none;
        }
        &[type="password"] {
            &::-ms-reveal {
                display: none;
            }
        }
    }

    &:hover:not(.disabled, .focus, .error, .readonly) {
        &.ghost {
            border-color: $stroke-soft-200;
        }
        &.stroke {
            border-color: $stroke-sub-300;
        }
        &.grey {
            background-color: $neutral-50;
        }
        &.grey2 {
            background-color: $neutral-100;
        }
        .icon-left {
            color: $icon-strong-900;
        }
    }

    &.empty {
        .ti {
            color: $text-soft-400;
        }
        input {
            font-weight: 500;
            color: $text-main-900;
        }
    }
    &.filled {
        .ti {
            color: $icon-sub-500;
        }
        input {
            font-weight: 500;
            color: $text-main-900;
        }
    }

    &.focus:not(.error) {
        &.ghost,
        &.stroke {
            border-color: $blue-base;
        }
        &.grey,
        &.grey2 {
            background-color: $bg-weak-100;
        }
    }
    &.xs {
        height: 28px;
        @include font-size-13;
        .ti {
            font-size: 14px;
        }
    }

    &.md {
        height: 32px;
        @include font-size-14;
        .ti {
            font-size: 16px;
        }
    }

    &.lg {
        height: 60px;
        @include font-size-32;
        .ti {
            font-size: 32px;
        }
    }

    &.multiline {
        height: auto;
        align-items: flex-start;
        align-content: flex-start;

        textarea {
            display: block;
            width: 100%;
            min-height: inherit;
            padding: 0;
            margin: 0;
            border: none;
            outline: none;
            resize: vertical;
            background-color: transparent;
            color: $text-main-900;
            font-weight: 500;
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;

            &::placeholder {
                color: $text-soft-400;
                opacity: 1;
            }
        }
    }

    &.error {
        border: 1px solid $red-base;
        .ti {
            color: $red-base;
        }
    }

    &.align-center {
        input {
            text-align: center;
        }
    }
    &.align-right {
        input {
            text-align: right;
        }
    }
}

input[type="time"]::-webkit-calendar-picker-indicator {
    display: none;
}
</style>
